/**
 * Public API key management.
 *
 * Three things in this file are load-bearing and easy to undo by accident:
 *
 * 1. The plaintext key must never reach the router (search params are
 *    serialized into the URL and the history entry), a cache, storage, a toast,
 *    `logError`, or any analytics call. Any of those turns a shown-once secret
 *    into a stored one.
 *
 *    Note that this is NOT satisfied by holding it in a `useState` and calling
 *    it done — an earlier version of this comment claimed exactly that and was
 *    wrong. `useMutation` keeps its own `data` in react-query's MutationCache,
 *    readable from any `queryClient` and visible in DevTools, so the secret was
 *    sitting in a global cache while the comment said it wasn't. What actually
 *    holds the property is the `gcTime: 0` / `reset()` pair below, and the
 *    tests that read `getMutationCache()` directly rather than inferring from
 *    the DOM. Prefer a probe to a re-reading of this paragraph.
 * 2. `isActive` comes from the server. `isApiKeyActive` lives in
 *    `@aica/database`, which ui-worker cannot import, so the list endpoint
 *    computes it. Re-deriving it here from `revokedAt`/`expiresAt` would create
 *    a third definition of "active" that drifts from the one the auth gate
 *    enforces. The revoked/expiry timestamps below are rendered as timestamps —
 *    never as a status.
 * 3. The list is fetched on every tier, the create form only on a paid one. An
 *    org that downgrades keeps its rows, and must still be able to see and
 *    revoke them; hiding them would leave credentials it can't reach.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  KeyRound,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  apiKeyListResponseSchema,
  apiKeyScopeSchema,
  createApiKeyResponseSchema,
  MAX_ACTIVE_API_KEYS_PER_ORG,
  type ApiKeyScope,
  type ApiKeySummary,
} from '@aica/api-types/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useFeatureFlagsSafe } from '@/contexts/FeatureFlagsContext';
import { usePermissions, useRole } from '@/contexts/RoleContext';
import { useTier } from '@/contexts/TierContext';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { getDocsUrl } from '@/lib/docs';
import { logError } from '@/lib/logger';
import { lockTier } from '@/lib/pro-trial';
import { CONTROL_MIN_HEIGHT } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

const API_KEYS_PATH = '/api/v1/settings/api-keys';

/** Offered at creation. The enum is the contract; this is its display order. */
const SELECTABLE_SCOPES: readonly ApiKeyScope[] = apiKeyScopeSchema.options;

export function ApiKeysSection() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { canSeeBilling } = usePermissions();
  const { selectedOrgId, isOrgSwitching } = useRole();
  // Same source the Plan & Billing tab reads — not a second tier lookup.
  const { tier, isLoading: tierLoading } = useTier();
  // The trial arrives with the flag map, which can land after the tier: until
  // the server answers, a trial org would see the upgrade prompt flip to the
  // create control. `answered`, not `isLoading`: a localStorage seed ends the
  // loading state without the trial, which only the server's answer carries.
  const { proTrial, answered: flagsAnswered } = useFeatureFlagsSafe();
  const { authenticatedFetch } = useAuthFetch();
  const queryClient = useQueryClient();

  const [issuedKey, setIssuedKey] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['records:write']);
  const [pendingRevoke, setPendingRevoke] = useState<ApiKeySummary | null>(null);

  // Mirrors the server's `getTierRank(gateTier(tier)) <= 0` gate: 'free' is
  // rank 0 and the only tier below a paid plan, and a Free organisation's Pro
  // trial lifts it to Pro (#2816). This is an affordance, not the gate — POST
  // answers 403 on its own if the tier or the trial changes under us.
  const isFree = lockTier(tier, proTrial) === 'free';
  // Only a stored Free tier can be lifted by the trial; a paid one needs no wait.
  const awaitingTrial = tier === 'free' && !flagsAnswered;

  const keysQuery = useQuery({
    queryKey: ['settings', 'api-keys', selectedOrgId],
    enabled: canSeeBilling && !!selectedOrgId,
    queryFn: async () => {
      const raw = await authenticatedFetch<unknown>(API_KEYS_PATH);
      const parsed = apiKeyListResponseSchema.safeParse(raw);
      if (!parsed.success) {
        // A contract drift renders identically to a network failure, so name it
        // in telemetry. Field paths only — no values: this payload is key
        // metadata, and the habit of logging the offending object is how a
        // secret ends up in a log line one refactor from now.
        logError('api_keys.list_contract_mismatch', {
          component: 'ApiKeysSection',
          issues: parsed.error.issues.map((issue) => issue.path.join('.')),
        });
        throw new Error('API key list did not match the contract');
      }
      return parsed.data.data;
    },
  });

  const createKey = useMutation({
    // The mutation's own `data` holds the plaintext, and react-query keeps
    // settled mutations in the MutationCache for gcTime — five minutes by
    // default — so the secret outlives both the dialog and the component
    // unless this says otherwise.
    //
    // `gcTime: 0` and the `reset()` on acknowledgement are jointly necessary,
    // not redundant, and the tests pin each one separately:
    //   - dropping `gcTime: 0` leaves the entry cached in BOTH cases, because
    //     reset() detaches the observer but gc is what evicts the entry;
    //   - dropping `reset()` leaves it cached while the section stays mounted,
    //     because a live observer keeps the mutation alive whatever gcTime says.
    gcTime: 0,
    mutationFn: async (input: { name: string; scopes: ApiKeyScope[] }) => {
      const raw = await authenticatedFetch<unknown>(API_KEYS_PATH, {
        method: 'POST',
        body: JSON.stringify(input),
      });
      return createApiKeyResponseSchema.parse(raw);
    },
    onSuccess: (result) => {
      setIssuedKey(result.plaintext);
      setName('');
      setScopes(['records:write']);
      void queryClient.invalidateQueries({
        queryKey: ['settings', 'api-keys', selectedOrgId],
      });
    },
    onError: (error) => {
      // No key material exists yet on this path, and none is referenced here.
      logError('api_keys.create_failed', {
        component: 'ApiKeysSection',
        error: error instanceof Error ? error.message : 'unknown',
      });
    },
  });

  const revokeKey = useMutation({
    mutationFn: (key: ApiKeySummary) =>
      authenticatedFetch(`${API_KEYS_PATH}/${key.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setPendingRevoke(null);
      void queryClient.invalidateQueries({
        queryKey: ['settings', 'api-keys', selectedOrgId],
      });
    },
    onError: (error, key) => {
      // Identifiers only: a key id and prefix are safe, the secret is not.
      logError('api_keys.revoke_failed', {
        component: 'ApiKeysSection',
        keyId: key.id,
        error: error instanceof Error ? error.message : 'unknown',
      });
    },
  });

  // Reloading or closing the tab destroys the only copy of the key. The browser
  // shows its own generic confirmation; ours is the panel behind it.
  useEffect(() => {
    if (!issuedKey) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [issuedKey]);

  // RBAC api_key.manage sits at the same privilege level as billing
  // (see packages/core/src/rbac/permissions.ts): org owner or client. For
  // anyone else the section does not exist.
  if (!canSeeBilling) return null;

  const keys = keysQuery.data ?? [];
  const activeCount = keys.filter((key) => key.isActive).length;
  const atLimit = activeCount >= MAX_ACTIVE_API_KEYS_PER_ORG;
  const actionsDisabled = !selectedOrgId || isOrgSwitching;
  const canSubmit =
    name.trim().length > 0 &&
    scopes.length > 0 &&
    !atLimit &&
    !actionsDisabled &&
    !createKey.isPending;

  const formatDate = (iso: string | null): string | null => {
    if (!iso) return null;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime())
      ? iso
      : parsed.toLocaleDateString(i18n.language);
  };

  return (
    <div className="space-y-4" data-testid="section-api-keys">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-primary" aria-hidden="true" />
        <h3 className="text-lg font-semibold text-fg">
          {t('publicApiKeys.title', 'API Keys')}
        </h3>
      </div>
      <p className="text-sm text-fg-muted">
        {t(
          'publicApiKeys.description',
          'Issue keys so your own systems can upload calls and read analyses through the public API.'
        )}
      </p>
      <a
        href={getDocsUrl('api', i18n.language)}
        target="_blank"
        rel="noopener noreferrer"
        className={cn(
          CONTROL_MIN_HEIGHT.sm,
          'inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:underline focus-visible:rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
        data-testid="api-keys-docs-link"
      >
        {t('publicApiKeys.docsLink', 'Read the API documentation')}
        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
      </a>

      {tierLoading || awaitingTrial ? (
        <Skeleton className="h-24 w-full" />
      ) : isFree ? (
        <div
          className="flex items-start gap-3 rounded-card border border-border bg-bg-surface p-4"
          data-testid="api-keys-upgrade-prompt"
        >
          <AlertCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-primary"
            aria-hidden="true"
          />
          <div className="space-y-1">
            <p className="text-sm font-medium text-fg">
              {t('publicApiKeys.upgradeRequired', 'The public API requires a paid plan.')}
            </p>
            <p className="text-sm text-fg-muted">
              {t(
                'publicApiKeys.upgradeHint',
                'Upgrade your plan in Plan & Billing to issue API keys.'
              )}
            </p>
          </div>
        </div>
      ) : (
        <CreateKeyForm
          name={name}
          scopes={scopes}
          atLimit={atLimit}
          canSubmit={canSubmit}
          isPending={createKey.isPending}
          onNameChange={setName}
          onToggleScope={(scope) =>
            setScopes((current) =>
              current.includes(scope)
                ? current.filter((item) => item !== scope)
                : [...current, scope]
            )
          }
          onSubmit={() => createKey.mutate({ name: name.trim(), scopes })}
        />
      )}

      {createKey.isError && (
        <ErrorNote
          message={
            createKey.error instanceof Error
              ? createKey.error.message
              : t('publicApiKeys.createFailed', 'Could not create the key.')
          }
        />
      )}

      {keysQuery.isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : keysQuery.isError ? (
        <ErrorNote message={t('publicApiKeys.loadFailed', 'Could not load API keys.')} />
      ) : keys.length === 0 ? (
        <p className="text-sm text-fg-muted" data-testid="api-keys-empty">
          {t('publicApiKeys.empty', 'No API keys yet.')}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="api-keys-list">
          {keys.map((key) => (
            <li
              key={key.id}
              className="flex items-start justify-between gap-4 rounded-card border border-border bg-bg-surface p-4"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-fg">{key.name}</span>
                  {/* Server-computed. Never re-derived from the timestamps below. */}
                  <span
                    className={
                      key.isActive
                        ? 'rounded-full bg-primary-dim px-2 py-0.5 text-xs text-primary-fg'
                        : 'rounded-full bg-bg-muted px-2 py-0.5 text-xs text-fg-muted'
                    }
                  >
                    {key.isActive
                      ? t('publicApiKeys.statusActive', 'Active')
                      : t('publicApiKeys.statusInactive', 'Inactive')}
                  </span>
                </div>
                <p className="truncate font-mono text-xs text-fg-muted">
                  {key.keyPrefix}…
                </p>
                <p className="text-xs text-fg-muted">{key.scopes.join(', ')}</p>
                <p className="text-xs text-fg-subtle">
                  {t('publicApiKeys.createdOn', 'Created {{date}}', {
                    date: formatDate(key.createdAt),
                  })}
                  {key.lastUsedAt
                    ? ` · ${t('publicApiKeys.lastUsedOn', 'last used {{date}}', {
                        date: formatDate(key.lastUsedAt),
                      })}`
                    : ` · ${t('publicApiKeys.neverUsed', 'never used')}`}
                  {key.revokedAt
                    ? ` · ${t('publicApiKeys.revokedOn', 'revoked {{date}}', {
                        date: formatDate(key.revokedAt),
                      })}`
                    : ''}
                  {key.expiresAt
                    ? ` · ${t('publicApiKeys.expiresOn', 'expires {{date}}', {
                        date: formatDate(key.expiresAt),
                      })}`
                    : ''}
                </p>
              </div>
              {key.isActive && (
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5 border-danger text-danger-fg hover:bg-danger-dim"
                  disabled={actionsDisabled || revokeKey.isPending}
                  onClick={() => setPendingRevoke(key)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('publicApiKeys.revoke', 'Revoke')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {issuedKey && (
        <IssuedKeyDialog
          plaintext={issuedKey}
          onAcknowledge={() => {
            setIssuedKey(null);
            // Half of what evicts the MutationCache copy — see `gcTime: 0` on
            // the mutation for why neither half suffices alone.
            createKey.reset();
          }}
        />
      )}

      {pendingRevoke && (
        <RevokeConfirmDialog
          keyName={pendingRevoke.name}
          pending={revokeKey.isPending}
          onCancel={() => !revokeKey.isPending && setPendingRevoke(null)}
          onConfirm={() => revokeKey.mutate(pendingRevoke)}
        />
      )}
    </div>
  );
}

function ErrorNote({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-card border border-danger bg-danger-dim p-3"
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
      <p className="text-sm text-fg">{message}</p>
    </div>
  );
}

interface CreateKeyFormProps {
  name: string;
  scopes: ApiKeyScope[];
  atLimit: boolean;
  canSubmit: boolean;
  isPending: boolean;
  onNameChange: (value: string) => void;
  onToggleScope: (scope: ApiKeyScope) => void;
  onSubmit: () => void;
}

function CreateKeyForm({
  name,
  scopes,
  atLimit,
  canSubmit,
  isPending,
  onNameChange,
  onToggleScope,
  onSubmit,
}: CreateKeyFormProps) {
  const { t } = useTranslation(['settings', 'common']);

  return (
    <form
      className="space-y-4 rounded-card border border-border bg-bg-surface p-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      <div className="space-y-2">
        <label htmlFor="api-key-name" className="text-sm font-medium text-fg">
          {t('publicApiKeys.nameLabel', 'Key name')}
        </label>
        <Input
          id="api-key-name"
          value={name}
          maxLength={64}
          autoComplete="off"
          placeholder={t('publicApiKeys.namePlaceholder', 'e.g. Billing system')}
          onChange={(event) => onNameChange(event.target.value)}
        />
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-fg">
          {t('publicApiKeys.scopesLabel', 'Permissions')}
        </legend>
        {SELECTABLE_SCOPES.map((scope) => (
          <div key={scope} className="flex items-center gap-2">
            <Checkbox
              id={`api-key-scope-${scope}`}
              checked={scopes.includes(scope)}
              onCheckedChange={() => onToggleScope(scope)}
            />
            <label
              htmlFor={`api-key-scope-${scope}`}
              className="font-mono text-sm text-fg-muted"
            >
              {scope}
            </label>
          </div>
        ))}
      </fieldset>

      {atLimit && (
        <p className="text-sm text-fg-muted" data-testid="api-keys-limit-note">
          {t(
            'publicApiKeys.limitReached',
            'You already hold the maximum of {{max}} active keys. Revoke one to issue another.',
            { max: MAX_ACTIVE_API_KEYS_PER_ORG }
          )}
        </p>
      )}

      <Button type="submit" disabled={!canSubmit}>
        {isPending && (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
        )}
        {t('publicApiKeys.create', 'Create key')}
      </Button>
    </form>
  );
}

/**
 * The only place the plaintext key is ever rendered.
 *
 * Deliberately not dismissible by backdrop click or Escape: both are reflexes,
 * and either one would discard a secret the server cannot reissue. Closing
 * takes an explicit press on a button that says what closing costs.
 */
function IssuedKeyDialog({
  plaintext,
  onAcknowledge,
}: {
  plaintext: string;
  onAcknowledge: () => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const containerRef = useFocusTrap<HTMLDivElement>(true);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      // Nothing about the failure is logged — the message would be the only
      // place the key could leak into telemetry.
      setCopyFailed(true);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-bg-void/60" />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="issued-key-title"
        className="relative z-10 w-full max-w-lg rounded-card border border-border bg-bg-elevated p-6 shadow-xl"
        data-testid="issued-key-dialog"
      >
        <h3 id="issued-key-title" className="text-lg font-semibold text-fg">
          {t('publicApiKeys.issuedTitle', 'Copy your API key now')}
        </h3>
        <p className="mt-2 text-sm text-fg">
          {t(
            'publicApiKeys.copyOnceWarning',
            'This is the only time this key will be shown. We store a hash, not the key itself — once you close this window it cannot be recovered, and reloading the page will lose it.'
          )}
        </p>

        <code
          className="mt-4 block select-all break-all rounded-card border border-border bg-bg-surface p-3 font-mono text-sm text-fg"
          data-testid="issued-key-plaintext"
        >
          {plaintext}
        </code>

        <div className="mt-3 flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={copy}>
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {copied
              ? t('publicApiKeys.copied', 'Copied')
              : t('publicApiKeys.copy', 'Copy')}
          </Button>
          {copyFailed && (
            <span className="text-sm text-fg-muted">
              {t('publicApiKeys.copyFailed', 'Copying failed — select the key manually.')}
            </span>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={onAcknowledge}>
            {t('publicApiKeys.acknowledge', "I've saved the key — close and discard it")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RevokeConfirmDialog({
  keyName,
  pending,
  onCancel,
  onConfirm,
}: {
  keyName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const containerRef = useFocusTrap<HTMLDivElement>(true);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-bg-void/60" onClick={onCancel} />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="revoke-key-title"
        className="relative z-10 w-full max-w-md rounded-card border border-border bg-bg-elevated p-6 shadow-xl"
      >
        <h3 id="revoke-key-title" className="text-lg font-semibold text-fg">
          {t('publicApiKeys.revokeTitle', 'Revoke {{name}}?', { name: keyName })}
        </h3>
        <p className="mt-2 text-sm text-fg-muted">
          {t(
            'publicApiKeys.revokeMessage',
            'Any integration using this key stops working immediately. Revoking cannot be undone.'
          )}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" size="sm" disabled={pending} onClick={onCancel}>
            {t('common:common.cancel', 'Cancel')}
          </Button>
          <Button variant="destructive" size="sm" disabled={pending} onClick={onConfirm}>
            {pending
              ? t('publicApiKeys.revoking', 'Revoking…')
              : t('publicApiKeys.confirmRevoke', 'Revoke key')}
          </Button>
        </div>
      </div>
    </div>
  );
}
